import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { KoszykCeny } from '@/hooks/useHistogramCen';
import { slupkiNaGranicach, zaznaczenieSlupka } from '@/lib/histogramSkala';
import { GRANICE_CENY, GRANICE_DO_BAZY } from '@/lib/skalaCeny';

/**
 * Słupki rozkładu cen nad suwakiem (etap J, wzór Airbnb).
 *
 * PO CO. Sam suwak nie mówi, gdzie jest rynek: „do 3 000 zł" może odcinać połowę ofert albo
 * ich ogon i nie da się tego odgadnąć. Słupki odpowiadają na to jednym spojrzeniem, zanim
 * człowiek ruszy uchwytem.
 *
 * SŁUPEK = KROK SUWAKA (28.09, skala nieliniowa). Koszyki liczy baza na granicach skali
 * (`GRANICE_CENY`), więc wszystkie słupki mają równą szerokość na ekranie, a uchwyt zawsze
 * stoi na granicy słupka. Zaznaczenie w trzech odcieniach — pełne, częściowe (słupek zachodzi
 * na zakres częścią: kwota z pola w jego środku albo słupek tuż pod „od X", który ma oferty za
 * równe X zł), brak. Reguła siedzi w `zaznaczenieSlupka`.
 *
 * WYSOKOŚĆ 48 PX i ani piksela więcej (zasada pulpitu z 27.09: nie powiększamy rozwijanego
 * „Cena" ponad potrzebę).
 *
 * SKALA WYSOKOŚCI JEST PIERWIASTKOWA, nie liniowa. Przy liniowej jeden koszyk ze szczytem
 * rozkładu spłaszcza całą resztę do kreski przy zerze — a pytanie brzmi „gdzie jest rynek",
 * nie „ile dokładnie jest w koszyku".
 */
const WYSOKOSC = 48;

const KOLOR: Record<ReturnType<typeof zaznaczenieSlupka>, string> = {
  pelne: 'bg-primary/70',
  czesciowe: 'bg-primary/30',
  brak: 'bg-muted-foreground/25',
};

export function HistogramCen({
  koszyki,
  rentMin,
  rentMax,
}: {
  koszyki: KoszykCeny[];
  /** Wybrany zakres jak w filtrze listy: inkluzywny, `undefined` = bez granicy. */
  rentMin?: number;
  rentMax?: number;
}) {
  const { t } = useTranslation();
  const slupki = useMemo(() => slupkiNaGranicach(koszyki, GRANICE_CENY), [koszyki]);
  const maks = useMemo(() => Math.max(1, ...slupki.map((s) => s.ile)), [slupki]);

  if (slupki.length === 0) return null;

  return (
    <div
      className="flex h-12 items-end"
      style={{ height: WYSOKOSC }}
      role="img"
      aria-label={t('search.filters.histogram_aria')}
      data-testid="histogram-cen"
      // Granice wysłane do bazy — `scripts/check-histogram.mjs` woła RPC dokładnie z nimi,
      // żeby bramka nie miała drugiej, własnej kopii skali.
      data-granice-do-bazy={GRANICE_DO_BAZY.join(',')}
    >
      {slupki.map((s, i) => {
        const zaznaczenie = zaznaczenieSlupka(i, slupki, rentMin, rentMax);
        const wysokosc = s.ile === 0 ? 0 : Math.max(2, Math.round((Math.sqrt(s.ile) / Math.sqrt(maks)) * WYSOKOSC));
        return (
          <span
            key={s.dol}
            className="flex-1"
            style={{ height: wysokosc }}
            data-dol={s.dol}
            data-gora={s.gora}
            data-ile={s.ile}
            data-zaznaczenie={zaznaczenie}
          >
            {/* Kreska odstępu marginesem, nie `gap` — szerokość słupka = szerokość kroku. */}
            <span className={`block h-full rounded-sm ${KOLOR[zaznaczenie]}`} style={{ marginRight: 1 }} />
          </span>
        );
      })}
    </div>
  );
}

export default HistogramCen;
